import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { PlayIcon, PauseIcon, BackwardIcon, ForwardIcon } from "@heroicons/react/24/solid";
import { ArrowTopRightOnSquareIcon, Bars3BottomLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import { api, CallDetailData, callParties, formatMs } from "./api";
import { useNowPlaying } from "./NowPlayingContext";

// Docked player driven by the shared audio element (NowPlayingContext). It
// shows the transcript a few lines at a time — the current line plus up to
// four before it for context, chosen with the 1–5 picker in the controls —
// and the block flips up as playback crosses into the next segment. Hidden
// while the playing call's own page is open — that page's transcript player
// drives the same element.

const SPEAKER_COLORS = ["bg-blue-500", "bg-green-500", "bg-purple-500", "bg-amber-500", "bg-red-500", "bg-pink-500"];

const LINES_KEY = "pnai-miniplayerLines";
const LINE_OPTIONS = [1, 2, 3, 4, 5];
function loadLines(): number {
  try {
    const v = Number(localStorage.getItem(LINES_KEY));
    return LINE_OPTIONS.includes(v) ? v : 1;
  } catch {
    return 1;
  }
}

const MiniPlayer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { audio, nowPlaying, playing, currentMs, attachSource, pendingStart, close, bounds, setDockHeight } = useNowPlaying();
  const [call, setCall] = useState<CallDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const investigationId = nowPlaying?.investigationId || "";
  const callId = nowPlaying?.callId || "";

  // The call's own page drives the same audio element; step aside there.
  const onCallPage = Boolean(matchPath("/investigations/:inv/calls/:call", location.pathname)?.params.call === callId);

  useEffect(() => {
    if (!callId) return;
    let cancelled = false;
    setCall(null);
    setError(null);
    api
      .call(investigationId, callId)
      .then((c) => {
        if (cancelled) return;
        setCall(c);
        if (c.audioUrl) {
          const start = pendingStart?.callId === callId ? pendingStart.startMs : undefined;
          attachSource(investigationId, callId, c.audioUrl, start != null ? { startMs: start, autoplay: true } : {});
        }
      })
      .catch(() => !cancelled && setError("Couldn't load this call"));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationId, callId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !onCallPage) close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close, onCallPage]);

  const speakers = useMemo(() => {
    const t = call?.transcript;
    if (!t) return {} as Record<string, { name: string; color: string }>;
    const labels = [...new Set(t.segments.map((s) => s.speaker))].sort();
    return Object.fromEntries(
      labels.map((label, i) => {
        const p = call?.participants?.find((x) => x.label === label);
        const name = p?.name || (p?.role && p.role !== "unknown" ? p.role.replace("_", " ") : null) || `Speaker ${i + 1}`;
        return [label, { name, color: SPEAKER_COLORS[i % SPEAKER_COLORS.length] }];
      })
    );
  }, [call]);

  const segments = useMemo(() => call?.transcript?.segments || [], [call]);
  const durationMs = call?.transcript?.durationMs || call?.duration_ms || 0;
  const activeIndex = useMemo(() => {
    const idx = segments.findIndex((s) => currentMs >= s.startMs && currentMs < s.endMs);
    if (idx >= 0) return idx;
    for (let i = segments.length - 1; i >= 0; i--) if (segments[i].startMs <= currentMs) return i;
    return -1;
  }, [segments, currentMs]);
  const seg = activeIndex >= 0 ? segments[activeIndex] : null;

  // How many transcript lines to show: the current one plus the lines just
  // before it, so a reader can catch the run-up to the cited moment.
  const [lines, setLines] = useState(loadLines);
  const pickLines = (n: number) => {
    setLines(n);
    try {
      localStorage.setItem(LINES_KEY, String(n));
    } catch {
      // Per-viewer convenience only.
    }
  };
  const visible = activeIndex >= 0 ? segments.slice(Math.max(0, activeIndex - (lines - 1)), activeIndex + 1) : [];

  // Animate the line area's height as lines of different length come and go.
  const lineRef = useRef<HTMLDivElement>(null);
  const [lineHeight, setLineHeight] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const el = lineRef.current;
    if (el) setLineHeight(el.getBoundingClientRect().height);
  }, [seg?.id, lines, call, error, bounds?.width]);
  const isEnglish = /^en/i.test(call?.transcript?.primaryLanguage || "en");

  // Publish how much of the pane's bottom the dock covers, so chat composers
  // and lists can pad above it. Zero whenever the dock isn't showing.
  const dockRef = useRef<HTMLDivElement>(null);
  const showing = Boolean(nowPlaying) && !onCallPage;
  useLayoutEffect(() => {
    if (!showing) {
      setDockHeight(0);
      return;
    }
    const el = dockRef.current;
    if (!el) return;
    const measure = () => setDockHeight(el.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      ro.disconnect();
      setDockHeight(0);
    };
  }, [showing, setDockHeight]);

  const seek = (ms: number, play = true) => {
    audio.currentTime = Math.max(0, ms) / 1000;
    if (play) audio.play().catch(() => {});
  };

  if (!nowPlaying || onCallPage) return null;

  const title = call ? callParties(call).primary : "Loading call…";
  const when = call?.call_started_at ? new Date(call.call_started_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

  return (
    <div ref={dockRef} className="fixed bottom-0 z-40 miniplayer-enter" style={bounds ? { left: bounds.left, width: bounds.width } : { left: 0, right: 0 }} role="region" aria-label="Now playing">
      <div className="mx-4 mb-4 rounded-2xl border border-base-300 bg-base-100/95 backdrop-blur shadow-2xl overflow-hidden">
        {/* Current line — outer box animates to the inner content's height */}
        <div className="overflow-hidden transition-[height] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]" style={{ height: lineHeight }}>
          <div ref={lineRef} className="px-5 py-3 flex items-start gap-3">
            {error ? (
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            ) : !call ? (
              <div className="animate-pulse h-5 w-2/3 rounded bg-base-300" />
            ) : seg ? (
              <div key={seg.id} className="flip-up min-w-0 flex-1 space-y-1">
                {visible.map((v) => {
                  const current = v.id === seg.id;
                  return (
                    <div
                      key={v.id}
                      className={cn("flex items-start gap-3 transition-opacity", !current && "opacity-50 hover:opacity-80 cursor-pointer")}
                      onClick={current ? undefined : () => seek(v.startMs, playing)}
                      title={current ? undefined : `Play from ${formatMs(v.startMs)}`}
                    >
                      <span className={cn("size-2.5 rounded-full shrink-0 mt-[8.5px]", speakers[v.speaker]?.color)} />
                      <p className={cn("leading-6 text-base-content min-w-0", current ? "text-base" : "text-sm")}>
                        <span className="text-xs font-semibold text-base-content/60 mr-1.5 align-baseline">{speakers[v.speaker]?.name}:</span>
                        {!isEnglish && v.english ? v.english : v.text}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-base-content/50 italic">…</p>
            )}
          </div>
        </div>

        {/* Controls, separated from the transcript line above */}
        <div className="px-4 py-3 flex items-center gap-2 flex-wrap border-t border-base-300 bg-base-200/60">
          <button type="button" className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors" onClick={() => seek(currentMs - 10000, playing)} title="Back 10s">
            <BackwardIcon className="h-4 w-4" />
          </button>
          <button type="button" className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full bg-blue-500 hover:bg-blue-600 text-white transition-colors" onClick={() => (playing ? audio.pause() : audio.play().catch(() => {}))} title={playing ? "Pause" : "Play"} disabled={!call?.audioUrl}>
            {playing ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4 ml-0.5" />}
          </button>
          <button type="button" className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors" onClick={() => seek(currentMs + 10000, playing)} title="Forward 10s">
            <ForwardIcon className="h-4 w-4" />
          </button>
          <span className="text-sm tabular-nums text-base-content/70 shrink-0">
            {formatMs(currentMs)} / {formatMs(durationMs)}
          </span>
          <input type="range" min={0} max={durationMs || 1} value={Math.min(currentMs, durationMs || currentMs)} onChange={(e) => seek(Number(e.target.value), playing)} className="inv-seek flex-1 min-w-32" style={{ "--p": `${durationMs ? (currentMs / durationMs) * 100 : 0}%` } as React.CSSProperties} />
          <div className="min-w-0 max-w-[16rem] hidden md:block">
            <p className="text-xs font-medium text-base-content truncate">{title}</p>
            {when && <p className="text-[11px] text-base-content/50 truncate">{when}</p>}
          </div>
          {/* Lines picker: one compact button showing the current count;
              each click steps 1 → 5 and wraps. */}
          <button
            type="button"
            onClick={() => pickLines((lines % LINE_OPTIONS.length) + 1)}
            title={`Showing ${lines} transcript line${lines === 1 ? "" : "s"} — click for ${(lines % LINE_OPTIONS.length) + 1}`}
            aria-label={`Transcript lines shown: ${lines}. Click to change.`}
            className="h-8 px-2.5 shrink-0 inline-flex items-center gap-1 rounded-full text-xs font-semibold tabular-nums bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
          >
            <Bars3BottomLeftIcon className="size-3.5" /> {lines}
          </button>
          <button
            type="button"
            className="h-8 px-2.5 shrink-0 inline-flex items-center gap-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
            onClick={() => navigate(`/investigations/${investigationId}/calls/${callId}`)}
            title="Open the full call — playback continues"
          >
            <ArrowTopRightOnSquareIcon className="size-3.5" /> Open call
          </button>
          <button type="button" className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full text-base-content/50 hover:bg-base-200 hover:text-base-content transition-colors" onClick={close} title="Stop and close (Esc)">
            <XMarkIcon className="size-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MiniPlayer;
